import { useEffect, useMemo, useRef, useState } from "react";
import { addDays, todayIn, zonedParts, type IsoDate } from "../../../shared/dates";
import { IDEA_KINDS, suggestDinner, type Suggestion } from "../../../shared/kitchen/dinnerIdeas";
import { featuredSlot, indexMeals, mealKey, SLOT_LABEL, slotsFor, weekDates, weekStartOf } from "../../../shared/kitchen/meals";
import type { Meal, MealInput, MealSlot, MealSlotsPreset, WeekStart } from "../../../shared/kitchen/models";
import type { Member } from "../../../shared/models";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useMembers } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { OutsideLink } from "../../ui/OutsideLink";
import { Sheet } from "../../ui/Sheet";
import { StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { fmtDate } from "../calendar/calendarModel";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useFavoriteActions, useKitchenSettings, useMealActions, useMealHistory, useMealPicks, useMeals } from "./api";
import { KGlyph, PlateArt } from "./glyphs";
import "./kitchen.css";
import { remPx } from "../sky/common";

interface MealsConfig {
  title: string;
  display: "week" | "tonight";
  slots: "household" | MealSlotsPreset;
  weekStart: "household" | WeekStart;
}

interface EditTarget {
  date: IsoDate;
  slot: MealSlot;
  meal: Meal | null;
  /** Opened from "Surprise me": start with a suggestion. */
  surprise?: boolean;
}

function useMealSetup(config: MealsConfig) {
  const settings = useKitchenSettings().data;
  const slotsPreset = config.slots === "household" ? (settings?.mealSlots ?? "dinner") : config.slots;
  const weekStart = config.weekStart === "household" ? (settings?.weekStart ?? "monday") : config.weekStart;
  return { slots: slotsFor(slotsPreset), weekStart };
}

function Meals(props: WidgetProps<MealsConfig>) {
  return props.config.display === "tonight" && !props.fullscreen ? <Tonight {...props} /> : <WeekPlanner {...props} />;
}

// ---------- Week planner ----------

function WeekPlanner({ config, editing, fullscreen, openFullscreen }: WidgetProps<MealsConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const { slots, weekStart } = useMealSetup(config);
  const [offset, setOffset] = useState(0);
  const first = addDays(weekStartOf(today, weekStart), offset * 7);
  const days = weekDates(first);
  const q = useMeals(first, days[6]!);
  const state = dataStateOf(q, 20 * 60_000);
  const members = useMembers().data ?? [];
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const byKey = useMemo(() => indexMeals(q.data ?? []), [q.data]);
  const [target, setTarget] = useState<EditTarget | null>(null);

  const range = `${fmtDate(first, { month: "short", day: "numeric" })} – ${fmtDate(days[6]!, first.slice(5, 7) === days[6]!.slice(5, 7) ? { day: "numeric" } : { month: "short", day: "numeric" })}`;
  const label = offset === 0 ? "This week" : offset === 1 ? "Next week" : offset === -1 ? "Last week" : range;

  return (
    <div className={`km slots-${slots.length}`}>
      <WidgetHeader
        title={config.title || "Meals"}
        meta={offset === 0 || offset === 1 || offset === -1 ? `${label} · ${range}` : range}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <>
            <button className="icon-btn" onClick={() => setOffset((o) => o - 1)} aria-label="Previous week" disabled={editing}>
              <Icon name="chevronLeft" />
            </button>
            {offset !== 0 && (
              <button className="btn btn-sm km-today" onClick={() => setOffset(0)} disabled={editing}>
                Today
              </button>
            )}
            <button className="icon-btn" onClick={() => setOffset((o) => o + 1)} aria-label="Next week" disabled={editing}>
              <Icon name="chevronRight" />
            </button>
          </>
        }
      />
      {!q.data && state === "loading" ? (
        <div className="widget-loading" />
      ) : (
        <ol className="km-week" aria-label={`Meals, ${range}`}>
          {days.map((d) => (
            <li key={d} className={`km-day ${d === today ? "is-today" : ""} ${d < today ? "is-past" : ""}`}>
              <div className="km-dayhead">
                <span className="km-dow">{d === today ? "Today" : fmtDate(d, { weekday: "short" })}</span>
                <span className="km-date">{fmtDate(d, { day: "numeric" })}</span>
              </div>
              <div className="km-slots">
                {slots.map((slot) => {
                  const meal = byKey.get(mealKey(d, slot)) ?? null;
                  const cook = meal?.cookId ? memberById.get(meal.cookId) : undefined;
                  return (
                    <div key={slot} className={`km-slot ${meal ? "has-meal" : "is-empty"}`}>
                      <button
                        className="km-slot-btn"
                        onClick={() => setTarget({ date: d, slot, meal })}
                        disabled={editing || !q.data}
                        aria-label={`${SLOT_LABEL[slot]}, ${fmtDate(d, { weekday: "long", month: "long", day: "numeric" })}: ${meal ? meal.title : "not planned"}`}
                      >
                        {slots.length > 1 && <span className="km-slot-name">{SLOT_LABEL[slot]}</span>}
                        {meal ? (
                          <span className="km-meal-title">{meal.title}</span>
                        ) : (
                          <span className="km-add">
                            <Icon name="plus" size="0.95rem" />
                            <span>Plan</span>
                          </span>
                        )}
                      </button>
                      {meal && (cook || meal.recipeUrl) && (
                        <span className="km-slot-meta">
                          {cook && <Avatar member={cook} size="1.5rem" />}
                          {meal.recipeUrl && (
                            <OutsideLink className="km-recipe" href={meal.recipeUrl} title={`Recipe: ${meal.title}`} ariaLabel={`Recipe for ${meal.title}`}>
                              <KGlyph name="external" size="0.95rem" />
                              <span>Recipe</span>
                            </OutsideLink>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {target && <MealEditor target={target} members={members} onClose={() => setTarget(null)} />}
    </div>
  );
}

// ---------- Tonight (compact Home card) ----------

function Tonight({ config, editing, size }: WidgetProps<MealsConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const { date: today, minutes } = zonedParts(now, timeZone);
  const tomorrow = addDays(today, 1);
  const { slots } = useMealSetup(config);
  const q = useMeals(today, tomorrow);
  const state = dataStateOf(q, 20 * 60_000);
  const members = useMembers().data ?? [];
  const byKey = useMemo(() => indexMeals(q.data ?? []), [q.data]);
  const [target, setTarget] = useState<EditTarget | null>(null);

  const slot = featuredSlot(minutes, slots);
  const meal = byKey.get(mealKey(today, slot)) ?? null;
  const next = byKey.get(mealKey(tomorrow, "dinner")) ?? null;
  const cook = meal?.cookId ? members.find((m) => m.id === meal.cookId) : undefined;
  const eyebrow = slot === "dinner" ? "Tonight" : `${SLOT_LABEL[slot]} today`;
  const tall = size.height > remPx() * 13;

  if (!q.data && state === "loading") return <div className="widget-loading" />;

  return (
    <div className={`km-tonight ${meal ? "" : "is-empty"}`}>
      <button className="km-tonight-main" onClick={() => setTarget({ date: today, slot, meal })} disabled={editing || !q.data} aria-label={meal ? `${eyebrow}: ${meal.title}. Tap to change.` : `Plan ${slot === "dinner" ? "tonight’s dinner" : SLOT_LABEL[slot].toLowerCase()}`}>
        <PlateArt empty={!meal} />
        <span className="km-tonight-text">
          <span className="km-eyebrow">{eyebrow}</span>
          <span className="km-tonight-title">{meal ? meal.title : "Nothing planned yet"}</span>
          {!meal && <span className="km-tonight-cta">Tap to plan {slot === "dinner" ? "dinner" : SLOT_LABEL[slot].toLowerCase()}</span>}
          {meal && cook && (
            <span className="km-tonight-sub">
              <Avatar member={cook} size="1.4rem" /> {cook.displayName} is cooking
            </span>
          )}
        </span>
      </button>
      {!meal && slot === "dinner" && !editing && q.data && (
        <div className="km-tonight-foot">
          <button className="btn btn-sm" onClick={() => setTarget({ date: today, slot, meal: null, surprise: true })}>
            <Icon name="dice" size="1rem" /> Surprise me
          </button>
        </div>
      )}
      {(meal?.recipeUrl || (tall && next)) && (
        <div className="km-tonight-foot">
          {meal?.recipeUrl && (
            <OutsideLink className="km-recipe" href={meal.recipeUrl} title={`Recipe: ${meal.title}`}>
              <KGlyph name="external" size="0.95rem" /> Recipe
            </OutsideLink>
          )}
          {tall && next && (
            <span className="km-tomorrow">
              Tomorrow: <b>{next.title}</b>
            </span>
          )}
        </div>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {target && <MealEditor target={target} members={members} onClose={() => setTarget(null)} />}
    </div>
  );
}

// ---------- Editor ----------

function MealEditor({ target, members, onClose }: { target: EditTarget; members: Member[]; onClose: () => void }) {
  const { isAdmin } = useHousehold();
  const { toast } = useAppUi();
  const actions = useMealActions();
  const favActions = useFavoriteActions();
  const { favorites, recent } = useMealPicks(true);
  const { timeZone } = useHousehold();
  const isDinner = target.slot === "dinner";
  const history = useMealHistory(isDinner);
  const [suggested, setSuggested] = useState<{ s: Suggestion; shown: string[] } | null>(null);
  const [title, setTitle] = useState(target.meal?.title ?? "");
  const [notes, setNotes] = useState(target.meal?.notes ?? "");
  const [url, setUrl] = useState(target.meal?.recipeUrl ?? "");
  const [cookId, setCookId] = useState<string | null>(target.meal?.cookId ?? null);
  const [error, setError] = useState<string | null>(null);

  const when = `${SLOT_LABEL[target.slot]} · ${fmtDate(target.date, { weekday: "long", month: "short", day: "numeric" })}`;
  const favList = favorites.data ?? [];
  const favTitles = new Set(favList.map((f) => f.title.toLowerCase()));
  const recentList = (recent.data ?? []).filter((r) => !favTitles.has(r.title.toLowerCase())).slice(0, 10);
  const isFavorite = favTitles.has(title.trim().toLowerCase());

  const pick = (p: { title: string; notes: string | null; recipeUrl: string | null }) => {
    setTitle(p.title);
    setNotes(p.notes ?? "");
    setUrl(p.recipeUrl ?? "");
    setError(null);
  };

  const save = () => {
    if (!title.trim()) {
      setError("Give the meal a name.");
      return;
    }
    const input: MealInput = { title: title.trim(), notes: notes.trim() || null, recipeUrl: url.trim() || null, cookId };
    // Optimistic: close at once; the card shows the change and rolls back with a toast if the save fails.
    actions.set.mutate({ date: target.date, slot: target.slot, input }, { onError: (e) => toast(`Couldn’t save “${input.title}”: ${e.message}`) });
    onClose();
  };

  const clear = () => {
    const prev = target.meal;
    actions.clear.mutate(
      { date: target.date, slot: target.slot },
      {
        onSuccess: () =>
          prev &&
          toast(`Cleared “${prev.title}”`, {
            label: "Undo",
            run: () => actions.set.mutate({ date: prev.date, slot: prev.slot, input: { title: prev.title, notes: prev.notes, recipeUrl: prev.recipeUrl, cookId: prev.cookId } }),
          }),
        onError: (e) => toast(`Couldn’t clear the meal: ${e.message}`),
      },
    );
    onClose();
  };

  // "Surprise me": the household's own favorites and past dinners mixed with popular ideas.
  const surprise = () => {
    const past = [
      ...favList.map((f) => ({ title: f.title, notes: f.notes, recipeUrl: f.recipeUrl, favorite: true })),
      ...(history.data ?? recent.data ?? []).filter((r) => !favTitles.has(r.title.toLowerCase())),
    ];
    const shown = [...(suggested?.shown ?? []), title.trim()].filter(Boolean).slice(-40);
    const s = suggestDinner(past, { today: todayIn(timeZone), skip: shown });
    if (!s) return;
    setSuggested({ s, shown });
    if (s.from === "ours") pick(s.meal);
    else pick({ title: s.idea.title, notes: null, recipeUrl: null });
  };
  const surprised = useRef(false);
  const picksReady = !history.isLoading && !favorites.isLoading;
  // Opened from "Surprise me": suggest once, as soon as the household's own picks have loaded.
  useEffect(() => {
    if (!target.surprise || surprised.current || !picksReady) return;
    surprised.current = true;
    surprise();
  }, [picksReady]);
  const suggestedNote =
    suggested && suggested.s.from === "ours"
      ? suggested.s.meal.favorite
        ? "⭐ One of your favorites"
        : `🍽️ You had this ${suggested.s.meal.lastDate ? fmtDate(suggested.s.meal.lastDate, { month: "short", day: "numeric" }) : "before"}`
      : suggested && suggested.s.from === "idea"
        ? `${IDEA_KINDS[suggested.s.idea.kind].emoji} ${IDEA_KINDS[suggested.s.idea.kind].label} idea`
        : null;

  const saveFavorite = () =>
    favActions.create.mutate(
      { title: title.trim(), notes: notes.trim() || null, recipeUrl: url.trim() || null },
      { onSuccess: (f) => toast(`Saved “${f.title}” to favorites`), onError: (e) => setError(e.message) },
    );

  return (
    <Sheet
      title={target.meal ? "Edit meal" : "Plan a meal"}
      onClose={onClose}
      footer={
        <>
          {target.meal && (
            <button className="btn btn-danger-ghost" onClick={clear}>
              <Icon name="trash" size="1.1rem" /> Clear
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form">
        <p className="km-when">{when}</p>
        <label className="field">
          <span>Meal</span>
          <div className="km-title-row">
            <input
              className="input input-lg"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              placeholder="e.g. Lemon herb chicken"
              maxLength={120}
              autoFocus={!target.meal}
            />
            {isAdmin && title.trim() && !isFavorite && (
              <button className="icon-btn km-fav-btn" onClick={saveFavorite} aria-label="Save to favorites" disabled={favActions.create.isPending}>
                <KGlyph name="star" />
              </button>
            )}
          </div>
        </label>
        {isDinner && (
          <div className="km-surprise">
            <button className="btn" onClick={surprise}>
              <Icon name="dice" size="1.15rem" /> {suggested ? "Another idea" : "Surprise me"}
            </button>
            {suggestedNote && title === (suggested!.s.from === "ours" ? suggested!.s.meal.title : suggested!.s.idea.title) && <span className="muted small">{suggestedNote}</span>}
          </div>
        )}
        {(favList.length > 0 || recentList.length > 0) && (
          <div className="field">
            <span>Quick pick</span>
            {favList.length > 0 && (
              <div className="chip-row wrap km-picks" aria-label="Favorites">
                {favList.map((f) => (
                  <button key={f.id} className={`chip ${title === f.title ? "on" : ""}`} onClick={() => pick(f)}>
                    <KGlyph name="star" size="0.9rem" className="km-star" />
                    {f.title}
                  </button>
                ))}
              </div>
            )}
            {recentList.length > 0 && (
              <div className="chip-row wrap km-picks" aria-label="Recent meals">
                {recentList.map((r) => (
                  <button key={r.title} className={`chip chip-sm ${title === r.title ? "on" : ""}`} onClick={() => pick(r)}>
                    {r.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {members.length > 0 && (
          <div className="field">
            <span>Who’s cooking</span>
            <div className="chip-row wrap">
              <button className={`chip ${cookId === null ? "on" : ""}`} onClick={() => setCookId(null)}>
                Not set
              </button>
              {members.map((m) => (
                <button key={m.id} className={`chip ${cookId === m.id ? "on" : ""}`} style={{ ["--member" as string]: m.color }} onClick={() => setCookId(m.id)}>
                  <i className="dot" />
                  {m.displayName}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="field">
          <span>Notes</span>
          <textarea className="input km-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} rows={2} placeholder="Sides, prep, who’s bringing what…" />
        </label>
        <label className="field">
          <span>Recipe link</span>
          <input className="input" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} placeholder="https://…" />
        </label>
        {error && <p className="error-text">{error}</p>}
      </div>
    </Sheet>
  );
}

export const mealsWidget: WidgetDefinition<MealsConfig> = {
  type: "meals",
  version: 1,
  name: "Meal planner",
  description: "The week’s dinners (or every meal) at a glance. Tap a day to plan it.",
  icon: "calendar",
  category: "household",
  defaultSize: { w: 8, h: 4 },
  minSize: { w: 3, h: 2 },
  stackHeight: (c) => (c.display === "tonight" ? 150 : 560),
  defaults: { title: "Meals", display: "week", slots: "household", weekStart: "household" },
  settings: [
    {
      key: "display",
      label: "Show",
      type: "select",
      options: [
        { value: "week", label: "Week planner" },
        { value: "tonight", label: "Tonight’s dinner (compact)" },
      ],
    },
    {
      key: "slots",
      label: "Meals",
      type: "select",
      options: [
        { value: "household", label: "Household default" },
        { value: "dinner", label: "Dinner only" },
        { value: "all", label: "Breakfast, lunch & dinner" },
      ],
    },
    {
      key: "weekStart",
      label: "Week starts on",
      type: "select",
      options: [
        { value: "household", label: "Household default" },
        { value: "monday", label: "Monday" },
        { value: "sunday", label: "Sunday" },
      ],
    },
    { key: "title", label: "Title", type: "text", maxLength: 30 },
  ],
  provider: "kinmantle-kitchen",
  supportsFullscreen: true,
  Component: Meals,
};
